import React, { useState } from 'react';
import styles from './enrichment-name-server-changes.module.sass';
import { Typography, Tooltip, Table } from 'antd';
import { RightOutlined, InfoCircleFilled } from '@ant-design/icons';
import { ColumnTitle } from '../../../../shared-components/ColumnTitle';
import ProgressBar from '../../../../shared-components/ProgressBar';
import LoadingSkeleton from '../../../../shared-components/LoadingSkeleton';
import cx from 'classnames';
import { formatDaysTag, formatDate } from '../../../../redux/utils/date';

function EnrichmentNameServerChanges({ newData, statusNew }) {
  const { Title, Text } = Typography;
  const nsChanges = newData?.response?.nschanges?.results_summary;
  const [expandState, setExpandState] = useState(false);

  function returnChangesDescription() {
    let descriptions = [];

    if (nsChanges?.has_change_circular) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain moved to a NS previously used
        </Text>,
      );
    }
    if (nsChanges?.has_change_expire_from) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain was on NS that hosts expired domains
        </Text>,
      );
    }
    if (nsChanges?.has_change_expire_to) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain moved to NS that hosts expired domains
        </Text>,
      );
    }
    if (nsChanges?.has_change_ns_in_domain_from) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain was on self-hosted (self-named) NS
        </Text>,
      );
    }
    if (nsChanges?.has_change_ns_in_domain_to) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain moved to self-hosted (self-named) NS
        </Text>,
      );
    }
    if (nsChanges?.has_change_ns_srv_domain_density_low_from) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain was on NS that only hosts a small number of domains
        </Text>,
      );
    }
    if (nsChanges?.has_change_ns_srv_domain_density_low_to) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain moved to NS that only hosts a small number of domains
        </Text>,
      );
    }
    if (nsChanges?.has_change_parked_from) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain was on NS that hosts parked domains
        </Text>,
      );
    }
    if (nsChanges?.has_change_parked_to) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain moved to NS that hosts parked domains
        </Text>,
      );
    }
    if (nsChanges?.has_change_sinkhole_from) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain was on NS that hosts sinkholes domains
        </Text>,
      );
    }
    if (nsChanges?.has_change_sinkhole_to) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain moved to NS that hosts sinkholes domains
        </Text>,
      );
    }

    return descriptions;
  }

  function returnLastSeenDescription() {
    let descriptions = [];

    if (nsChanges?.last_change_circular_to) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain moved to a NS previously used
        </Text>,
      );
    }
    if (nsChanges?.last_change_expire_from) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain was on NS that hosts expired domains
        </Text>,
      );
    }
    if (nsChanges?.last_change_expire_to) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain moved to NS that hosts expired domains
        </Text>,
      );
    }
    if (nsChanges?.last_change_ns_in_domain_from) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain was on self-hosted (self-named) NS
        </Text>,
      );
    }
    if (nsChanges?.last_change_ns_in_domain_to) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain moved to self-hosted (self-named) NS
        </Text>,
      );
    }
    if (nsChanges?.last_change_ns_srv_domain_density_low_from) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain was on NS that only hosts a small number of domains
        </Text>,
      );
    }
    if (nsChanges?.last_change_ns_srv_domain_density_low_to) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain moved to NS that only hosts a small number of domains
        </Text>,
      );
    }
    if (nsChanges?.last_change_parked_from) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain was on NS that hosts parked domains
        </Text>,
      );
    }
    if (nsChanges?.last_change_parked_to) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain moved to NS that hosts parked domains
        </Text>,
      );
    }
    if (nsChanges?.last_change_sinkhole_from) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain was on NS that hosts sinkholes domains
        </Text>,
      );
    }
    if (nsChanges?.last_change_sinkhole_to) {
      descriptions.push(
        <Text className={styles.double_line}>
          Domain moved to NS that hosts sinkholes domains
        </Text>,
      );
    }

    return descriptions;
  }

  const columns = [
    {
      title: <ColumnTitle columnName="Name" />,
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: <ColumnTitle columnName="Value" />,
      dataIndex: 'value',
      key: 'value',
    },
    {
      dataIndex: 'progress',
      key: 'progress',
      width: 100,
    },
    {
      dataIndex: 'expandIcon',
      key: 'expandIcon',
      align: 'right',
    },
  ];

  function returnTableData({ loading }) {
    return [
      {
        key: 1,
        name: loading ? (
          <LoadingSkeleton />
        ) : (
          <Text>
            NS Entropy{' '}
            <Tooltip title="A score that includes recency, frequency, and the number of NS changes">
              <InfoCircleFilled />
            </Tooltip>
          </Text>
        ),
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {' '}
            <Text strong>
              {nsChanges?.ns_entropy_score ? nsChanges?.ns_entropy_score : '0'}{' '}
            </Text>
            <Text className="ms-05">
              {nsChanges?.ns_entropy_score ? 'score' : null}
            </Text>{' '}
          </>
        ),
        progress: loading ? (
          <LoadingSkeleton />
        ) : (
          <ProgressBar
            bgcolor={nsChanges?.ns_entropy_score}
            width={'72'}
            completed={nsChanges?.ns_entropy_score}
          />
        ),
      },
      {
        key: 2,
        name: loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <Text>
              Number of Changes{' '}
              <Tooltip title="The number of times the domain has changed its name servers">
                <InfoCircleFilled />
              </Tooltip>
            </Text>
            {nsChanges && returnChangesDescription()}
          </>
        ),
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <Text strong>
            {nsChanges?.num_changes_all ? nsChanges?.num_changes_all : '0'}
          </Text>
        ),
        children: [
          {
            key: 21,
            name: <Text>Changes in last 7 days</Text>,
            value: (
              <Text strong>
                {nsChanges?.changes_last_7_days
                  ? nsChanges?.changes_last_7_days
                  : '0'}
              </Text>
            ),
            expandable: false,
            progress: null,
          },
          {
            key: 22,
            name: <Text>Changes in last 30 days</Text>,
            value: (
              <Text strong>
                {nsChanges?.changes_last_30_days
                  ? nsChanges?.changes_last_30_days
                  : '0'}
              </Text>
            ),
            expandable: false,
            progress: null,
          },
          {
            key: 23,
            name: <Text>Changes in last 90 days</Text>,
            value: (
              <Text strong>
                {nsChanges?.changes_last_90_days
                  ? nsChanges?.changes_last_90_days
                  : '0'}
              </Text>
            ),
            expandable: false,
            progress: null,
          },
          {
            key: 24,
            name: <Text>Total number of changes</Text>,
            value: (
              <Text strong>
                {nsChanges?.num_changes_all ? nsChanges?.num_changes_all : '0'}
              </Text>
            ),
          },
        ],
        progress: null,
        expandIcon: (
          <Text strong>
            <RightOutlined id="2" className={styles.expand_indicator} />
          </Text>
        ),
      },
      {
        key: 3,
        name: loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <Text>
              Last Change{' '}
              <Tooltip title="The last time a nameserver change occured">
                <InfoCircleFilled />
              </Tooltip>
            </Text>
            <br />
            <Text>Ago</Text>
            {nsChanges && returnLastSeenDescription()}
          </>
        ),
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <Text strong>
              {nsChanges?.last_change
                ? formatDate(nsChanges?.last_change)
                : '-'}
            </Text>
            <br />
            <Text strong>
              {nsChanges?.last_change_days_ago
                ? nsChanges?.last_change_days_ago
                : '-'}{' '}
            </Text>
            <Text>
              {nsChanges?.last_change_days_ago
                ? formatDaysTag(nsChanges?.last_change_days_ago, true)
                : ''}
            </Text>
          </>
        ),
        progress: null,
      },
    ];
  }

  return (
    <div
      id="enrichmentNameServerChanges"
      className={styles.enrichment_name_server_changes}
    >
      <div className={styles.title}>
        <Title level={4}>Name Server Changes</Title>
        <Text>
          The number of changes that have occurred related to a specific
          nameserver.&nbsp;
          <Text strong>Silent Push invention</Text>
        </Text>
      </div>

      <Table
        columns={columns}
        pagination={false}
        className={cx({ [`${styles.table_expanded}`]: expandState })}
        expandable={{
          rowExpandable: (record) => record?.children !== undefined,
          expandIcon: ({ expanded, onExpand, record }) => {
            if (expanded && record?.expandIcon) {
              if (document.getElementById(record?.key) && record?.expandIcon) {
                setExpandState(false);
                document.getElementById(record?.key).classList.add('expanded');
              }
            } else {
              if (document.getElementById(record?.key) && record?.expandIcon) {
                setExpandState(false);
                document
                  .getElementById(record?.key)
                  .classList.remove('expanded');
              }
            }
            return <></>;
          },
          expandRowByClick: true,
        }}
        dataSource={returnTableData({ loading: statusNew === 'loading' })}
      />
    </div>
  );
}

export default EnrichmentNameServerChanges;
